/**
 * Project-wise equal division of ONE original item target across DISTINCT vendors.
 * Key: project + normalized item name. Do not use vendor rate, quantity or PO count
 * as a divisor: the business rule is an equal split per distinct vendor.
 * Remainder paise go deterministically to sorted vendor IDs.
 * Pure function: does not modify its input.
 */
export const targetItemKey = (name) =>
  String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("en");

const cents = (value) => Math.max(0, Math.round(Number(value || 0) * 100));
const rupees = (value) => Number((value / 100).toFixed(2));
const distribute = (totalCents, count) => {
  const share = Math.floor(totalCents / count);
  const remainder = totalCents % count;
  return Array.from({ length: count }, (_, i) => share + Number(i < remainder));
};

export function allocateProjectTargets(inputPOs, canonicalBudgets = new Map()) {
  const pos = (inputPOs || []).map((po) => ({
    ...po,
    items: (po.items || []).map((item) => ({ ...item })),
  }));
  const groups = new Map();

  for (const po of pos) {
    const projectKey = String(po.projectName || "")
      .trim()
      .toLocaleLowerCase("en");
    const vendorId = String(po.vendorId || po._id || "");
    for (const [index, item] of po.items.entries()) {
      const itemKey = targetItemKey(item.itemName);
      if (!projectKey || !vendorId || !itemKey) continue;
      const key = JSON.stringify([projectKey, itemKey]);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ po, item, index, vendorId });
    }
  }

  for (const occurrences of groups.values()) {
    // Once saved, originalTargetCost is the budget source of truth.
    // For pre-migration records without it, use the largest stored target,
    // not their sum, to avoid double counting an original 5,000 twice.
    const originals = occurrences
      .map(({ item }) => cents(item.originalTargetCost))
      .filter((value) => value > 0);
    const legacy = occurrences
      .flatMap(({ item }) => [
        cents(item.vendorTargetCost),
        cents(item.targetCost),
      ])
      .filter((value) => value > 0);
    const groupFirst = occurrences[0];
    const budgetKey = JSON.stringify([
      String(groupFirst.po.projectName || "")
        .trim()
        .toLocaleLowerCase("en"),
      targetItemKey(groupFirst.item.itemName),
    ]);
    const canonicalCents = Number(canonicalBudgets.get(budgetKey) || 0);
    // A historic saved ZERO is missing data, not a valid override of a positive
    // original from the other vendor. Never invent a budget when all inputs are 0.
    const originalCents =
      canonicalCents > 0
        ? canonicalCents
        : originals.length > 0
          ? Math.max(...originals)
          : Math.max(0, ...legacy);

    const byVendor = new Map();
    for (const entry of occurrences) {
      if (!byVendor.has(entry.vendorId)) byVendor.set(entry.vendorId, []);
      byVendor.get(entry.vendorId).push(entry);
    }
    const vendorIds = [...byVendor.keys()].sort();
    const vendorShares = distribute(originalCents, vendorIds.length);
    for (const [vendorIndex, vendorId] of vendorIds.entries()) {
      const entries = byVendor.get(vendorId);
      const rowShares = distribute(vendorShares[vendorIndex], entries.length);
      for (const [rowIndex, { item }] of entries.entries()) {
        const allocated = rupees(rowShares[rowIndex]);
        const vendorAmount = Number(
          item.vendorAmount ??
            Number(item.vendorQty || 0) * Number(item.vendorRate || 0),
        );
        item.originalTargetCost = rupees(originalCents);
        item.vendorTargetCost = allocated;
        item.targetCost = allocated;
        item.targetVendorCount = vendorIds.length;
        item.targetMissing =
          originalCents <= 0 &&
          !occurrences.some(
            ({ item: sourceItem }) => sourceItem.isComplimentary === true,
          );
        item.difference = Number((allocated - vendorAmount).toFixed(2));
      }
    }
  }
  return pos;
}
